import React, { useState, useEffect } from 'react';
import api from './api';

const RoughPurchase = () => {
    // 1. STATE: Lists for Dropdowns
    const [partyList, setPartyList] = useState([]);
    const [brokerList, setBrokerList] = useState([]);

    // 2. STATE: Form Data
    const [formData, setFormData] = useState({
        purchaseDate: new Date().toISOString().split('T')[0], 
        sourceType: 'Local',
        billType: 'Cash', 
        paymentTermsDays: '', // <--- FIXED NAME (Was payTerms)
        
        partyId: '',
        brokerId: '',
        
        mainLotName: '', 
        origin: '',
        diamondType: 'Mix',
        sieveSize: '',
        assortment: 'Normal',
        
        currency: 'INR',
        exchangeRate: 1,
        
        weight: '',
        rate: '',
        
        discountPercent: 0,
        gstPercent: 0, 
        tdsPercent: 0,    
        brokeragePercent: 0,
        otherExpenses: 0,
    });

    // 3. STATE: Calculated Totals
    const [totals, setTotals] = useState({
        baseAmount: 0,
        discountAmount: 0,
        netAmount: 0,
        gstAmount: 0,
        tdsAmount: 0,
        brokerageAmount: 0,
        finalBillAmount: 0,
        partyPayable: 0,
        landedCost: 0,
        costPerCarat: 0,
        dueDateDisplay: '' 
    });

    // --- EFFECT 1: Load Dropdowns ---
    useEffect(() => {
        const loadMasters = async () => {
            try {
                const p = await api.get('/parties');
                setPartyList(p.data);
                const b = await api.get('/brokers');
                setBrokerList(b.data);
            } catch (err) { console.error("Failed to load masters"); }
        };
        loadMasters();
    }, []);

    // --- EFFECT 2: Auto-Fill TDS (Section 194Q) ---
    useEffect(() => {
        if (!formData.partyId || formData.sourceType !== 'Local' || formData.billType !== 'Bill') return;

        const w = parseFloat(formData.weight) || 0;
        const r = parseFloat(formData.rate) || 0;
        const currentDealValue = w * r;

        const selectedParty = partyList.find(p => p._id === formData.partyId);
        
        if (selectedParty) {
            const historicalTotal = selectedParty.currentFyTotalPurchases || 0;
            const grandTotal = historicalTotal + currentDealValue;

            if (grandTotal > 5000000) {
                if (parseFloat(formData.tdsPercent) === 0) {
                    setFormData(prev => ({ ...prev, tdsPercent: 0.1 }));
                }
            }
        }
    }, [formData.weight, formData.rate, formData.partyId, partyList, formData.billType, formData.sourceType]);

    // --- EFFECT 3: Logic Defaults ---
    useEffect(() => {
        // Auto-Fill Party Defaults
        if (formData.partyId) {
             const p = partyList.find(x => x._id === formData.partyId);
             if (p) {
                 // Only set if user hasn't manually changed it yet (simple check)
                 // or force it every time party changes
             }
        }

        // Rule: Import -> Default USD & No Tax
        if (formData.sourceType === 'Import') {
            setFormData(prev => ({ 
                ...prev, 
                currency: 'USD',
                gstPercent: 0, 
                tdsPercent: 0 
            }));
        } 

        // Rule: Cash -> Zero Taxes
        if (formData.billType === 'Cash') {
            setFormData(prev => ({ ...prev, gstPercent: 0, tdsPercent: 0 }));
        } else if (formData.billType === 'Bill' && formData.sourceType === 'Local' && formData.gstPercent === 0) {
             setFormData(prev => ({ ...prev, gstPercent: 0.25 }));
        }
    }, [formData.sourceType, formData.billType, formData.partyId]); // Added partyId dependency

    // --- EFFECT 4: Math Engine & Due Date ---
    useEffect(() => {
        const { weight, rate, exchangeRate, discountPercent, gstPercent, tdsPercent, brokeragePercent, otherExpenses, currency, purchaseDate, paymentTermsDays } = formData;

        const w = parseFloat(weight) || 0;
        const r = parseFloat(rate) || 0;
        const ex = currency === 'USD' ? (parseFloat(exchangeRate) || 1) : 1; 

        // 1. Math
        const base = w * r * ex;
        const discAmt = base * (parseFloat(discountPercent) / 100);
        const net = base - discAmt;
        const gstAmt = net * (parseFloat(gstPercent) / 100);
        const tdsAmt = net * (parseFloat(tdsPercent) / 100);
        const brokAmt = net * (parseFloat(brokeragePercent) / 100); 
        const other = parseFloat(otherExpenses) || 0;

        const bill = net + gstAmt;
        const payable = (bill - tdsAmt) + other;
        const landed = net + brokAmt + other;
        const perCarat = w > 0 ? (landed / w) : 0;

        // 2. Due Date Calculation (DD-MM-YYYY)
        let dueStr = '';
        if (purchaseDate && paymentTermsDays) {
            const d = new Date(purchaseDate);
            d.setDate(d.getDate() + parseInt(paymentTermsDays));
            dueStr = d.toLocaleDateString('en-GB'); // DD/MM/YYYY
        }

        setTotals({
            baseAmount: base.toFixed(2),
            discountAmount: discAmt.toFixed(2),
            netAmount: net.toFixed(2),
            gstAmount: gstAmt.toFixed(2),
            tdsAmount: tdsAmt.toFixed(2),
            brokerageAmount: brokAmt.toFixed(2),
            finalBillAmount: bill.toFixed(2),
            partyPayable: payable.toFixed(2),
            landedCost: landed.toFixed(2),
            costPerCarat: perCarat.toFixed(2),
            dueDateDisplay: dueStr
        });

    }, [formData]);

    // --- HANDLERS ---
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Auto-Fill Brokerage
        if (name === 'brokerId') {
            const selectedBroker = brokerList.find(b => b._id === value);
            if (selectedBroker) {
                setFormData(prev => ({ 
                    ...prev, 
                    brokerId: value,
                    brokeragePercent: selectedBroker.defaultBrokeragePercent || 0 
                }));
            }
        }
        
        // Auto-Fill Party Defaults
        if (name === 'partyId') {
            const selectedParty = partyList.find(p => p._id === value);
            if (selectedParty) {
                setFormData(prev => ({
                    ...prev,
                    partyId: value,
                    sourceType: selectedParty.defaultSourceType || 'Local',
                    billType: selectedParty.defaultBillType || 'Cash'
                }));
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formData.currency === 'USD' && formData.exchangeRate <= 1) {
            alert("Warning: Please enter a valid Exchange Rate for USD purchase.");
            return;
        }
        try {
            const res = await api.post('/rough', formData);
            alert(`✅ Purchase Locked! ID: ${res.data.data.rpNumber}`);
            // Optional: Reset form here
        } catch (error) {
            console.error(error);
            alert('❌ Error: ' + (error.response?.data?.error || error.message));
        }
    };

    return (
        <div className="container mt-4 mb-5">
            <div className="card shadow border-0">
                <div className="card-header bg-dark text-white d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Rough Inward (Purchase)</h5>
                    <small>Draft</small>
                </div>
                <div className="card-body bg-light">
                    <form onSubmit={handleSubmit}>
                        
                        {/* SECTION 1: HEADER */}
                        <div className="row mb-3">
                            <div className="col-md-3">
                                <label className="small fw-bold">Date</label>
                                <input type="date" name="purchaseDate" className="form-control form-control-sm" value={formData.purchaseDate} onChange={handleChange} required />
                            </div>
                            <div className="col-md-2">
                                <label className="small fw-bold">Source</label>
                                <select name="sourceType" className="form-select form-select-sm" onChange={handleChange} value={formData.sourceType}>
                                    <option value="Local">Local</option>
                                    <option value="Import">Import</option>
                                </select>
                            </div>
                            <div className="col-md-2">
                                <label className="small fw-bold">Bill Type</label>
                                <select name="billType" className="form-select form-select-sm" onChange={handleChange} value={formData.billType}>
                                    <option value="Cash">Cash</option>
                                    <option value="Bill">Bill (Tax)</option>
                                </select>
                            </div>
                            <div className="col-md-3">
                                <label className="small fw-bold">Pay Terms (Days)</label>
                                <div className="input-group input-group-sm">
                                    <input 
                                        type="number" 
                                        name="paymentTermsDays"  // <--- FIXED NAME
                                        className="form-control" 
                                        onChange={handleChange} 
                                        placeholder="Days" 
                                        value={formData.paymentTermsDays}
                                    />
                                </div>
                                {/* Due Date Display */}
                                {totals.dueDateDisplay && (
                                    <small className="text-danger fw-bold d-block mt-1">
                                        Due: {totals.dueDateDisplay}
                                    </small>
                                )}
                            </div>
                             <div className="col-md-2">
                                <label className="small fw-bold">Currency</label>
                                <select name="currency" className="form-select form-select-sm" onChange={handleChange} value={formData.currency}>
                                    <option value="INR">INR (₹)</option>
                                    <option value="USD">USD ($)</option>
                                </select>
                            </div>
                        </div>

                        {/* SECTION 2: PARTY & GOODS */}
                        <div className="card p-3 mb-3 border-0 shadow-sm bg-white">
                            <div className="row mb-2">
                                <div className="col-md-4">
                                    <label className="small text-muted">Seller (Party)</label>
                                    <select name="partyId" className="form-select" onChange={handleChange} required>
                                        <option value="">Select Party...</option>
                                        {partyList.map(p => (
                                            <option key={p._id} value={p._id}>{p.companyName} ({p.name})</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-4">
                                    <label className="small text-muted">Broker</label>
                                    <select name="brokerId" className="form-select" onChange={handleChange}>
                                        <option value="">Select Broker...</option>
                                        {brokerList.map(b => (
                                            <option key={b._id} value={b._id}>{b.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-4">
                                    <label className="small text-muted">Main Lot (Optional)</label>
                                    <input type="text" name="mainLotName" className="form-control" placeholder="Lot Name..." onChange={handleChange} />
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-3">
                                    <label className="small text-muted">Origin</label>
                                    <input type="text" name="origin" className="form-control form-control-sm" onChange={handleChange} />
                                </div>
                                <div className="col-md-3">
                                    <label className="small text-muted">Type</label>
                                    <input type="text" name="diamondType" className="form-control form-control-sm" placeholder="e.g. Mix" onChange={handleChange} />
                                </div>
                                <div className="col-md-3">
                                    <label className="small text-muted">Sieve Size</label>
                                    <input type="text" name="sieveSize" className="form-control form-control-sm" placeholder="+11, -6.5" onChange={handleChange} />
                                </div>
                                <div className="col-md-3">
                                    <label className="small text-muted">Assortment</label>
                                    <select name="assortment" className="form-select form-select-sm" onChange={handleChange}>
                                        <option value="Normal">Normal</option>
                                        <option value="Good">Good</option>
                                        <option value="Weak">Weak</option>
                                        <option value="Mix">Mix</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* SECTION 3: METRICS */}
                        <div className="row mb-3">
                            <div className="col-md-3">
                                <label className="fw-bold text-primary">Weight (Cts)</label>
                                <input type="number" step="0.01" name="weight" className="form-control" onChange={handleChange} required />
                            </div>
                            <div className="col-md-3">
                                <label className="fw-bold text-primary">Rate {formData.currency === 'USD' ? '($)' : '(₹)'}</label>
                                <input type="number" step="0.01" name="rate" className="form-control" onChange={handleChange} required />
                            </div>
                            
                            {formData.currency === 'USD' ? (
                                <div className="col-md-3">
                                    <label className="text-danger fw-bold">Ex. Rate</label>
                                    <input type="number" step="0.01" name="exchangeRate" className="form-control border-danger" onChange={handleChange} />
                                </div>
                            ) : (
                                <div className="col-md-3">
                                    <label className="text-muted">Ex. Rate</label>
                                    <input type="text" value="1.00" disabled className="form-control bg-light" />
                                </div>
                            )}

                            <div className="col-md-3">
                                <label>Discount %</label>
                                <input type="number" step="0.01" name="discountPercent" className="form-control" onChange={handleChange} />
                            </div>
                        </div>

                        {/* SECTION 4: TAXES & CHARGES */}
                        <div className="row mb-3 bg-white p-2 border rounded mx-1">
                            <div className="col-md-3">
                                <label className="small">GST %</label>
                                <input type="number" step="0.01" name="gstPercent" value={formData.gstPercent} className="form-control form-control-sm" onChange={handleChange} disabled={formData.billType === 'Cash' || formData.sourceType === 'Import'} />
                            </div>
                            <div className="col-md-3">
                                <label className="small text-danger">TDS % (194Q)</label>
                                <input type="number" step="0.1" name="tdsPercent" value={formData.tdsPercent} className="form-control form-control-sm" onChange={handleChange} disabled={formData.billType === 'Cash' || formData.sourceType === 'Import'} />
                            </div>
                            <div className="col-md-3">
                                <label className="small">Brokerage %</label>
                                <input type="number" step="0.01" name="brokeragePercent" value={formData.brokeragePercent} className="form-control form-control-sm" onChange={handleChange} />
                            </div>
                            <div className="col-md-3">
                                <label className="small">Other Exp.</label>
                                <input type="number" step="1" name="otherExpenses" className="form-control form-control-sm" onChange={handleChange} />
                                <small className="text-muted" style={{fontSize: '0.65rem'}}>*Adds to Payable</small>
                            </div>
                        </div>

                        {/* SECTION 5: TOTALS */}
                        <div className="alert alert-secondary">
                            {/* TOP ROW: BASE & TAXES */}
                            <div className="row text-center mb-3">
                                <div className="col-md-6 border-end">
                                    <small className="d-block text-muted">Base Amount</small>
                                    <strong>{totals.baseAmount}</strong>
                                </div>
                                <div className="col-md-6">
                                    <small className="d-block text-muted">Taxes (GST - TDS)</small>
                                    <span className="text-danger small">{totals.gstAmount} - {totals.tdsAmount}</span>
                                </div>
                            </div>
                            
                            {/* BOTTOM ROW: PAYABLES */}
                            <div className="row text-center">
                                {/* Broker Payable Box */}
                                <div className="col-md-4">
                                    <div className="card bg-white border-secondary">
                                        <div className="card-body py-2">
                                            <small className="text-muted">Broker Payable</small>
                                            <h5 className="mb-0 text-dark">{totals.brokerageAmount}</h5>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Party Payable Box */}
                                <div className="col-md-4">
                                    <div className="card bg-success text-white">
                                        <div className="card-body py-2">
                                            <small>Party Payable</small>
                                            <h5 className="mb-0">{totals.partyPayable}</h5>
                                        </div>
                                    </div>
                                </div>

                                {/* Landed Cost Box */}
                                <div className="col-md-4">
                                    <div className="card bg-secondary text-white">
                                        <div className="card-body py-2">
                                            <small>Landed Cost / Ct</small>
                                            <h5 className="mb-0">{totals.costPerCarat}</h5>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <button type="submit" className="btn btn-dark w-100 p-2 fw-bold text-uppercase">
                            Save Purchase
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default RoughPurchase;